/**
 * HTML utility functions for encoding/decoding HTML entities and handling HTML content
 */

/**
 * Decodes HTML entities and URI-encoded content with support for legacy encoding
 * Handles multiple encoding layers and converts <br> tags to newlines
 * 
 * @param text - The encoded text to decode
 * @returns Decoded text string
 */
export const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  
  let decoded = text;
  
  // First, handle legacy %uXXXX encoding (from old escape() function)
  decoded = decoded.replaceAll(/%u([0-9A-Fa-f]{4})/g, (_, hex) => {
    return String.fromCodePoint(Number.parseInt(hex, 16));
  });
  
  // Try to decode URI components - decode multiple times if needed
  let attempts = 0;
  const maxAttempts = 5; // Prevent infinite loops
  
  while (decoded.includes('%') && attempts < maxAttempts) {
    try {
      const beforeDecode = decoded;
      decoded = decodeURIComponent(decoded);
      
      // If nothing changed, break to avoid infinite loop
      if (beforeDecode === decoded) {
        break;
      }
      attempts++;
    } catch {
      // Failed to decode, stop trying
      break;
    }
  }
  
  // Replace <br> tags with newlines
  decoded = decoded.replaceAll(/<br\s*\/?>/gi, '\n');
  
  // Decode HTML entities using DOM
  try {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    decoded = textarea.value;
  } catch {
    // Silently fail
  }
  
  return decoded;
};